import React, { useEffect, useState, useMemo } from 'react';
import Layout from '../components/Layout';
import { designMappings } from '../services/api';
import {
  Search, Trash2, Edit3, X, Check, Link2, AlertTriangle,
  ArrowRight, Sparkles, CheckSquare, Square, Loader2, RefreshCw, ChevronDown, ChevronRight
} from 'lucide-react';

interface ShopifyProduct {
  id: number; title: string; product_type: string; collection: string;
  collections: string[]; is_mapped: boolean; mapped_article: string | null;
  mapped_color: string | null; mapped_to: string | null; image: string | null;
}
interface Blank { article: string; color: string; label: string; }
interface Mapping {
  id: number; shopify_title: string; blank_article: string; blank_color: string;
  notes: string | null; has_inventory_match: boolean; created_at: string | null;
}

const DesignMappings: React.FC = () => {
  const [tab, setTab] = useState<'map' | 'unmatched' | 'mapped'>('map');
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [collections, setCollections] = useState<string[]>([]);
  const [blanks, setBlanks] = useState<Blank[]>([]);
  const [mappings, setMappings] = useState<Mapping[]>([]);
  const [stats, setStats] = useState({ total: 0, mapped: 0, unmapped: 0 });
  const [activeCollection, setActiveCollection] = useState('all');
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [assignments, setAssignments] = useState<Record<string, { article: string; color: string }>>({});
  const [bulkBlank, setBulkBlank] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editId, setEditId] = useState<number | null>(null);
  const [editArticle, setEditArticle] = useState('');
  const [editColor, setEditColor] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [unmatchedItems, setUnmatchedItems] = useState<{ product_title: string; units: number; has_mapping: boolean }[]>([]);
  const [unmatchedLoading, setUnmatchedLoading] = useState(false);
  const [unmatchedStats, setUnmatchedStats] = useState({ total_titles: 0, total_units: 0 });

  const loadMappings = async () => { try { const r = await designMappings.getAll(); setMappings(r.data); } catch {} };
  const loadBlanks = async () => { try { const r = await designMappings.getInventoryBlanks(); setBlanks(r.data.all || []); } catch {} };
  const loadProducts = async () => {
    setProductsLoading(true);
    try {
      const r = await designMappings.getShopifyProducts();
      setProducts(r.data.products || []); setCollections(r.data.collections || []);
      setStats({ total: r.data.total, mapped: r.data.mapped, unmapped: r.data.unmapped });
    } catch (e: any) { setError('Failed to fetch Shopify products: ' + (e.response?.data?.detail || e.message)); }
    setProductsLoading(false);
  };

  const loadUnmatched = async () => {
    setUnmatchedLoading(true);
    try {
      const r = await designMappings.getUnmatchedLineItems();
      setUnmatchedItems(r.data.items || []);
      setUnmatchedStats({ total_titles: r.data.total_titles, total_units: r.data.total_units });
    } catch {}
    setUnmatchedLoading(false);
  };

  useEffect(() => { setLoading(true); Promise.all([loadMappings(), loadBlanks(), loadProducts(), loadUnmatched()]).finally(() => setLoading(false)); }, []);

  const showMsg = (msg: string, type: 'ok' | 'err' = 'ok') => {
    if (type === 'ok') { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); }
    else { setError(msg); setTimeout(() => setError(''), 4000); }
  };

  const filteredProducts = useMemo(() => {
    let list = products;
    if (activeCollection !== 'all') list = list.filter(p => p.collection === activeCollection);
    if (tab === 'map') list = list.filter(p => !p.is_mapped);
    if (filter) { const q = filter.toLowerCase(); list = list.filter(p => p.title.toLowerCase().includes(q)); }
    return list;
  }, [products, activeCollection, tab, filter]);

  const groupedProducts = useMemo(() => {
    const groups: Record<string, ShopifyProduct[]> = {};
    filteredProducts.forEach(p => { const c = p.collection || 'Other'; if (!groups[c]) groups[c] = []; groups[c].push(p); });
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b));
  }, [filteredProducts]);

  const setAssignment = (title: string, article: string, color: string) => setAssignments(p => ({ ...p, [title]: { article, color } }));
  const removeAssignment = (title: string) => setAssignments(p => { const n = { ...p }; delete n[title]; return n; });
  const toggleSelect = (title: string) => setSelected(p => { const n = new Set(p); if (n.has(title)) n.delete(title); else n.add(title); return n; });

  const selectAllVisible = () => {
    const titles = filteredProducts.filter(p => !p.is_mapped).map(p => p.title);
    setSelected(prev => prev.size === titles.length ? new Set() : new Set(titles));
  };

  const applyBulkBlank = () => {
    if (!bulkBlank) return;
    const [article, color] = bulkBlank.split(' — ');
    if (!article || !color) return;
    const n = { ...assignments };
    selected.forEach(t => { n[t] = { article: article.trim(), color: color.trim() }; });
    setAssignments(n);
    showMsg(`Applied "${bulkBlank}" to ${selected.size} products`);
  };

  const saveAssignments = async () => {
    const entries = Object.entries(assignments);
    if (!entries.length) { showMsg('No mappings to save', 'err'); return; }
    setSaving(true);
    try {
      const data = entries.map(([title, { article, color }]) => ({ shopify_title: title, blank_article: article, blank_color: color }));
      const r = await designMappings.createBulk(data);
      showMsg(`${r.data.created} created, ${r.data.updated} updated`);
      setAssignments({}); setSelected(new Set());
      await Promise.all([loadMappings(), loadProducts(), loadUnmatched()]);
    } catch (e: any) { showMsg(e.response?.data?.detail || 'Save failed', 'err'); }
    setSaving(false);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this mapping?')) return;
    try { await designMappings.delete(id); showMsg('Deleted'); await Promise.all([loadMappings(), loadProducts()]); }
    catch { showMsg('Delete failed', 'err'); }
  };

  const startEdit = (m: Mapping) => { setEditId(m.id); setEditArticle(m.blank_article); setEditColor(m.blank_color); };
  const saveEdit = async () => {
    if (!editId) return;
    try { await designMappings.update(editId, { blank_article: editArticle, blank_color: editColor }); showMsg('Updated'); setEditId(null); await loadMappings(); }
    catch (e: any) { showMsg(e.response?.data?.detail || 'Failed', 'err'); }
  };

  const toggleCollapse = (c: string) => setCollapsed(p => { const n = new Set(p); if (n.has(c)) n.delete(c); else n.add(c); return n; });
  const assignmentCount = Object.keys(assignments).length;
  const blankOpts = blanks.map(b => `${b.article} — ${b.color}`);

  return (
    <Layout title="Design Mappings" subtitle="Map Shopify designs to inventory blanks">
      {success && <div className="animate-in" style={{ padding: '10px 14px', background: 'var(--accent-green-soft)', border: '1px solid rgba(45,212,160,0.2)', borderRadius: 'var(--radius-sm)', color: 'var(--accent-green)', fontSize: 13, fontWeight: 500, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}><Check size={14} /> {success}</div>}
      {error && <div className="animate-in" style={{ padding: '10px 14px', background: 'var(--accent-red-soft)', border: '1px solid rgba(239,90,111,0.2)', borderRadius: 'var(--radius-sm)', color: 'var(--accent-red)', fontSize: 13, fontWeight: 500, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}><AlertTriangle size={14} /> {error}</div>}

      {/* KPI */}
      <div className="grid-4 animate-in" style={{ marginBottom: 16 }}>
        {[
          { label: 'Total Products', value: stats.total, color: 'var(--accent-blue)', bg: 'var(--accent-blue-soft)', icon: Link2 },
          { label: 'Mapped', value: stats.mapped, color: 'var(--accent-green)', bg: 'var(--accent-green-soft)', icon: Check },
          { label: 'Unmapped', value: stats.unmapped, color: 'var(--accent-amber)', bg: 'var(--accent-amber-soft)', icon: AlertTriangle },
          { label: 'Pending Save', value: assignmentCount, color: 'var(--accent-purple)', bg: 'var(--accent-purple-soft)', icon: Sparkles },
        ].map((k, i) => (
          <div key={i} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 36, height: 36, borderRadius: 'var(--radius-sm)', background: k.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><k.icon size={16} color={k.color} /></div>
            <div><div className="kpi-value" style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>{k.value}</div><div style={{ fontSize: 10.5, color: 'var(--text-secondary)' }}>{k.label}</div></div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="animate-in-1" style={{ display: 'flex', gap: 4, marginBottom: 14, flexWrap: 'wrap' }}>
        {([
          { key: 'map', label: `Map Products (${stats.unmapped})` },
          { key: 'unmatched', label: `Unmatched Orders (${unmatchedStats.total_titles})` },
          { key: 'mapped', label: `Mapped (${stats.mapped})` },
        ] as { key: 'map' | 'unmatched' | 'mapped'; label: string }[]).map(({ key, label }) => (
          <button key={key} onClick={() => { setTab(key); setFilter(''); setActiveCollection('all'); }} style={{
            padding: '9px 18px', borderRadius: 'var(--radius-sm)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            border: `1px solid ${tab === key ? 'rgba(79,125,249,0.3)' : 'var(--border)'}`,
            background: tab === key ? 'var(--accent-blue-soft)' : 'var(--bg-card)',
            color: tab === key ? 'var(--accent-blue)' : 'var(--text-secondary)',
          }}>{label}</button>
        ))}
        <div style={{ flex: 1 }} />
        <button onClick={() => { loadProducts(); loadMappings(); }} disabled={productsLoading} style={{
          padding: '9px 14px', background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 500, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 6,
        }}><RefreshCw size={13} className={productsLoading ? 'spin' : ''} /> Refresh</button>
      </div>

      {/* Search + Collection filter */}
      <div className="animate-in-1" style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <Search size={14} color="var(--text-muted)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
          <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Search products..."
            style={{ width: '100%', padding: '9px 12px 9px 34px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 13, outline: 'none' }} />
        </div>
        <select value={activeCollection} onChange={e => setActiveCollection(e.target.value)}
          style={{ padding: '9px 12px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 13, outline: 'none', minWidth: 160 }}>
          <option value="all">All Collections ({filteredProducts.length})</option>
          {collections.map(c => { const n = products.filter(p => p.collection === c && (tab !== 'map' || !p.is_mapped)).length; return <option key={c} value={c}>{c} ({n})</option>; })}
        </select>
      </div>

      {/* ═══ MAP TAB ═══ */}
      {tab === 'map' && (
        <>
          {filteredProducts.length > 0 && (
            <div className="animate-in-2" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '12px 16px', marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button onClick={selectAllVisible} style={{ padding: '6px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                {selected.size > 0 && selected.size === filteredProducts.filter(p => !p.is_mapped).length ? <><CheckSquare size={13} /> Deselect All</> : <><Square size={13} /> Select All ({filteredProducts.filter(p => !p.is_mapped).length})</>}
              </button>
              {selected.size > 0 && (
                <>
                  <span style={{ fontSize: 12, color: 'var(--accent-blue)', fontWeight: 600 }}>{selected.size} selected</span>
                  <ArrowRight size={12} color="var(--text-muted)" />
                  <select value={bulkBlank} onChange={e => setBulkBlank(e.target.value)} style={{ padding: '6px 10px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 12, outline: 'none' }}>
                    <option value="">Choose blank...</option>
                    {blankOpts.map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                  <button onClick={applyBulkBlank} disabled={!bulkBlank} style={{ padding: '6px 14px', background: bulkBlank ? 'var(--accent-blue)' : 'var(--bg-elevated)', border: 'none', borderRadius: 'var(--radius-sm)', color: bulkBlank ? '#fff' : 'var(--text-muted)', fontSize: 12, fontWeight: 600, cursor: bulkBlank ? 'pointer' : 'default' }}>Apply</button>
                </>
              )}
              <div style={{ flex: 1 }} />
              {assignmentCount > 0 && (
                <button onClick={saveAssignments} disabled={saving} style={{ padding: '8px 20px', background: 'linear-gradient(135deg, var(--accent-green), #059669)', border: 'none', borderRadius: 'var(--radius-sm)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={14} />} Save {assignmentCount}
                </button>
              )}
            </div>
          )}

          {(loading || productsLoading) ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}><Loader2 size={20} style={{ animation: 'spin 1s linear infinite', marginBottom: 8 }} /><br />Loading Shopify products...</div>
          ) : groupedProducts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--accent-green)', fontSize: 14, fontWeight: 600 }}><Check size={24} style={{ marginBottom: 8 }} /><br />All products are mapped!</div>
          ) : groupedProducts.map(([col, prods]) => {
            const isCol = collapsed.has(col);
            return (
              <div key={col} className="animate-in-2" style={{ marginBottom: 10 }}>
                <div onClick={() => toggleCollapse(col)} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: isCol ? 'var(--radius-md)' : 'var(--radius-md) var(--radius-md) 0 0', padding: '11px 16px', display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                  {isCol ? <ChevronRight size={15} color="var(--text-muted)" /> : <ChevronDown size={15} color="var(--text-muted)" />}
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{col}</span>
                  <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: 'var(--accent-amber-soft)', color: 'var(--accent-amber)' }}>{prods.length}</span>
                </div>
                {!isCol && (
                  <div style={{ border: '1px solid var(--border)', borderTop: 'none', borderRadius: '0 0 var(--radius-md) var(--radius-md)', overflow: 'hidden' }}>
                    {prods.map(p => {
                      const a = assignments[p.title];
                      const isSel = selected.has(p.title);
                      return (
                        <div key={p.id} style={{ padding: '8px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, background: a ? 'rgba(45,212,160,0.06)' : 'var(--bg-card)' }}>
                          <button onClick={() => toggleSelect(p.title)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', flexShrink: 0 }}>
                            {isSel ? <CheckSquare size={15} color="var(--accent-blue)" /> : <Square size={15} color="var(--text-muted)" />}
                          </button>
                          {p.image ? <img src={p.image} alt="" style={{ width: 30, height: 30, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} /> : <div style={{ width: 30, height: 30, borderRadius: 6, background: 'var(--bg-elevated)', flexShrink: 0 }} />}
                          <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</div>
                          <select value={a ? `${a.article} — ${a.color}` : ''} onChange={e => { if (!e.target.value) { removeAssignment(p.title); return; } const [ar, co] = e.target.value.split(' — '); setAssignment(p.title, ar.trim(), co.trim()); }}
                            style={{ padding: '5px 8px', background: a ? 'var(--accent-green-soft)' : 'var(--bg-input)', border: `1px solid ${a ? 'rgba(45,212,160,0.3)' : 'var(--border)'}`, borderRadius: 'var(--radius-sm)', color: a ? 'var(--accent-green)' : 'var(--text-primary)', fontSize: 11.5, outline: 'none', minWidth: 155, fontWeight: a ? 600 : 400, flexShrink: 0 }}>
                            <option value="">Select blank...</option>
                            {blankOpts.map(b => <option key={b} value={b}>{b}</option>)}
                          </select>
                          {a && <button onClick={() => removeAssignment(p.title)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', flexShrink: 0 }}><X size={13} color="var(--text-muted)" /></button>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      {/* ═══ UNMATCHED ORDERS TAB ═══ */}
      {tab === 'unmatched' && (
        <>
          {unmatchedLoading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 13 }}>
              <Loader2 size={20} style={{ animation: 'spin 1s linear infinite', marginBottom: 8 }} /><br />Loading unmatched items...
            </div>
          ) : unmatchedItems.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--accent-green)', fontSize: 14, fontWeight: 600 }}>
              <Check size={24} style={{ marginBottom: 8 }} /><br />No unmatched line items!
            </div>
          ) : (
            <>
              <div className="animate-in-2" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '12px 16px', marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <button onClick={() => {
                  const titles = unmatchedItems.filter(i => !i.has_mapping).map(i => i.product_title);
                  setSelected(prev => prev.size === titles.length ? new Set() : new Set(titles));
                }} style={{ padding: '6px 12px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}>
                  {selected.size > 0 && selected.size === unmatchedItems.filter(i => !i.has_mapping).length
                    ? <><CheckSquare size={13} /> Deselect All</>
                    : <><Square size={13} /> Select All ({unmatchedItems.filter(i => !i.has_mapping).length})</>}
                </button>
                {selected.size > 0 && (
                  <>
                    <span style={{ fontSize: 12, color: 'var(--accent-blue)', fontWeight: 600 }}>{selected.size} selected</span>
                    <ArrowRight size={12} color="var(--text-muted)" />
                    <select value={bulkBlank} onChange={e => setBulkBlank(e.target.value)} style={{ padding: '6px 10px', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 12, outline: 'none' }}>
                      <option value="">Choose blank...</option>
                      {blankOpts.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                    <button onClick={applyBulkBlank} disabled={!bulkBlank} style={{ padding: '6px 14px', background: bulkBlank ? 'var(--accent-blue)' : 'var(--bg-elevated)', border: 'none', borderRadius: 'var(--radius-sm)', color: bulkBlank ? '#fff' : 'var(--text-muted)', fontSize: 12, fontWeight: 600, cursor: bulkBlank ? 'pointer' : 'default' }}>Apply</button>
                  </>
                )}
                <div style={{ flex: 1 }} />
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{unmatchedStats.total_units.toLocaleString('en-IN')} units · {unmatchedStats.total_titles} products</span>
                {assignmentCount > 0 && (
                  <button onClick={saveAssignments} disabled={saving} style={{ padding: '8px 20px', background: 'linear-gradient(135deg, var(--accent-green), #059669)', border: 'none', borderRadius: 'var(--radius-sm)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                    {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={14} />} Save {assignmentCount}
                  </button>
                )}
              </div>

              <div className="animate-in-2" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                {unmatchedItems.map((item, idx) => {
                  const a = assignments[item.product_title];
                  const isSel = selected.has(item.product_title);
                  return (
                    <div key={item.product_title} style={{ padding: '8px 14px', borderBottom: idx < unmatchedItems.length - 1 ? '1px solid var(--border)' : 'none', display: 'flex', alignItems: 'center', gap: 10, background: a ? 'rgba(45,212,160,0.06)' : item.has_mapping ? 'rgba(99,102,241,0.04)' : 'var(--bg-card)' }}>
                      {!item.has_mapping ? (
                        <button onClick={() => toggleSelect(item.product_title)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', flexShrink: 0 }}>
                          {isSel ? <CheckSquare size={15} color="var(--accent-blue)" /> : <Square size={15} color="var(--text-muted)" />}
                        </button>
                      ) : <div style={{ width: 15, flexShrink: 0 }} />}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.product_title}</div>
                        {item.has_mapping && <div style={{ fontSize: 11, color: 'var(--accent-blue)', fontWeight: 500, marginTop: 1 }}>Mapping exists — will resolve on next Rematch</div>}
                      </div>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, flexShrink: 0, minWidth: 55, textAlign: 'right' }}>{item.units} units</span>
                      {!item.has_mapping && (
                        <>
                          <select value={a ? `${a.article} — ${a.color}` : ''} onChange={e => { if (!e.target.value) { removeAssignment(item.product_title); return; } const [ar, co] = e.target.value.split(' — '); setAssignment(item.product_title, ar.trim(), co.trim()); }}
                            style={{ padding: '5px 8px', background: a ? 'var(--accent-green-soft)' : 'var(--bg-input)', border: `1px solid ${a ? 'rgba(45,212,160,0.3)' : 'var(--border)'}`, borderRadius: 'var(--radius-sm)', color: a ? 'var(--accent-green)' : 'var(--text-primary)', fontSize: 11.5, outline: 'none', minWidth: 155, fontWeight: a ? 600 : 400, flexShrink: 0 }}>
                            <option value="">Select blank...</option>
                            {blankOpts.map(b => <option key={b} value={b}>{b}</option>)}
                          </select>
                          {a && <button onClick={() => removeAssignment(item.product_title)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'flex', flexShrink: 0 }}><X size={13} color="var(--text-muted)" /></button>}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}

      {/* ═══ MAPPED TAB ═══ */}
      {tab === 'mapped' && (
        <div className="animate-in-2" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
          <div className="table-responsive">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg-elevated)' }}>
                  {['Shopify Product', 'Blank Article', 'Color', 'Inventory', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: 10.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, borderBottom: '1px solid var(--border)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {mappings.length === 0 ? (
                  <tr><td colSpan={5} style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No mappings yet</td></tr>
                ) : mappings.filter(m => !filter || m.shopify_title.toLowerCase().includes(filter.toLowerCase())).map(m => (
                  <tr key={m.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={{ padding: '10px 14px', fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{m.shopify_title}</td>
                    <td style={{ padding: '10px 14px' }}>
                      {editId === m.id ? (
                        <select value={`${editArticle} — ${editColor}`} onChange={e => { const [a, c] = e.target.value.split(' — '); setEditArticle(a.trim()); setEditColor(c.trim()); }}
                          style={{ padding: '5px 8px', background: 'var(--bg-input)', border: '1px solid var(--accent-blue)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', fontSize: 12, outline: 'none' }}>
                          {blankOpts.map(b => <option key={b} value={b}>{b}</option>)}
                        </select>
                      ) : <span style={{ background: 'var(--accent-blue-soft)', color: 'var(--accent-blue)', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500 }}>{m.blank_article}</span>}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      {editId !== m.id && <span style={{ background: 'var(--bg-elevated)', color: 'var(--text-primary)', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500, border: '1px solid var(--border)' }}>{m.blank_color}</span>}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      {m.has_inventory_match ? <span style={{ color: 'var(--accent-green)', fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}><Check size={13} /> Match</span>
                        : <span style={{ color: 'var(--accent-red)', fontSize: 12, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}><AlertTriangle size={13} /> No match</span>}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {editId === m.id ? (<>
                          <button onClick={saveEdit} style={{ width: 28, height: 28, borderRadius: 'var(--radius-sm)', border: '1px solid rgba(45,212,160,0.3)', background: 'var(--accent-green-soft)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Check size={13} color="var(--accent-green)" /></button>
                          <button onClick={() => setEditId(null)} style={{ width: 28, height: 28, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-elevated)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={13} color="var(--text-muted)" /></button>
                        </>) : (<>
                          <button onClick={() => startEdit(m)} style={{ width: 28, height: 28, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-elevated)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Edit3 size={12} color="var(--text-secondary)" /></button>
                          <button onClick={() => handleDelete(m.id)} style={{ width: 28, height: 28, borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--bg-elevated)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Trash2 size={12} color="var(--accent-red)" /></button>
                        </>)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Floating save bar */}
      {assignmentCount > 0 && tab === 'map' && (
        <div style={{ position: 'fixed', bottom: 20, left: '50%', transform: 'translateX(-50%)', background: 'var(--bg-card)', border: '1px solid var(--accent-green)', borderRadius: 'var(--radius-lg)', padding: '12px 24px', boxShadow: '0 8px 32px rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', gap: 14, zIndex: 50 }}>
          <Sparkles size={16} color="var(--accent-green)" />
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{assignmentCount} mapping{assignmentCount !== 1 ? 's' : ''} ready</span>
          <button onClick={() => setAssignments({})} style={{ padding: '7px 14px', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>Clear</button>
          <button onClick={saveAssignments} disabled={saving} style={{ padding: '7px 20px', background: 'linear-gradient(135deg, var(--accent-green), #059669)', border: 'none', borderRadius: 'var(--radius-sm)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={14} />} Save All
          </button>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } .spin { animation: spin 1s linear infinite; }`}</style>
    </Layout>
  );
};

export default DesignMappings;
